import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Printer, QrCode, Trophy, Loader2, Check, UserPlus, X, Trash2, LockKeyhole, ShieldCheck, LogOut } from 'lucide-react';
import { heroImages } from '../mock';
import { api, clearToken, getToken, me } from '../lib/api';

const ROSTER_KEYS = ['u7', 'u9', 'u11', 'u13', 'peewee'];

const EQUIPMENT_COLUMNS = [
  { key: 'helmet', label: 'Helmet' },
  { key: 'shoulderPad', label: 'Shoulder Pad' },
  { key: 'jersey', label: 'Jersey #' },
  { key: 'practicePants', label: 'Practice Pants' },
  { key: 'mouthPiece', label: 'Mouth Piece' }
];

const emptyRow = () => ({
  position: '',
  jerseyNumber: '',
  helmet: false,
  shoulderPad: false,
  jersey: false,
  practicePants: false,
  mouthPiece: false,
  dateIssued: '',
  dateReturned: '',
  playerInit: '',
  issuerInit: '',
  feeHalfPaid: false,
  feePaidFull: false,
  notes: '',
  dob: '',
  city: '',
  contactPhone: '',
  emergencyContactName: ''
});

export default function Roster() {
  const location = useLocation();
  const initialKey = (() => {
    const q = new URLSearchParams(location.search).get('team');
    return ROSTER_KEYS.includes((q || '').toLowerCase()) ? q.toLowerCase() : 'u13';
  })();
  const [activeKey, setActiveKey] = useState(initialKey);
  const [rows, setRows] = useState({}); // { u7: [...], u9: [...], ... }
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [addedPlayersByTeam, setAddedPlayersByTeam] = useState({}); // { u7: [{last, first}], ... }
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPlayer, setNewPlayer] = useState({ first: '', last: '' });
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [detailIdx, setDetailIdx] = useState(null); // player row index whose detail is open
  const [viewer, setViewer] = useState(null);
  const [access, setAccess] = useState('checking');
  const [rosterConfig, setRosterConfig] = useState(null);
  const saveTimers = useRef({}); // debounce per (team+player)

  const rosterTeamOrder = rosterConfig?.order || ROSTER_KEYS;
  const rosterTeams = rosterConfig?.teams || {};
  const rosterMeta = rosterConfig || {};
  const activeTeam = rosterTeams[activeKey] || { key: activeKey, label: '', division: '', teamId: '' };
  const canEditRoster = viewer?.roles?.includes('roster_editor') || false;

  useEffect(() => {
    if (!getToken()) { setAccess('signed-out'); return; }
    me().then((profile) => {
      const allowed = profile.roles?.includes('coach') || profile.roles?.includes('roster_editor');
      setViewer(profile);
      setAccess(allowed ? 'allowed' : 'denied');
    }).catch(() => { clearToken(); setViewer(null); setAccess('signed-out'); });
  }, []);

  useEffect(() => {
    if (access !== 'allowed') return;
    api.get('/roster/metadata').then(({ data }) => setRosterConfig(data)).catch((error) => {
      if ([401, 403].includes(error.response?.status)) setAccess(error.response?.status === 401 ? 'signed-out' : 'denied');
    });
  }, [access]);

  // Sync tab with ?team=... in URL
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('team');
    if (q && ROSTER_KEYS.includes(q.toLowerCase()) && q.toLowerCase() !== activeKey) {
      setActiveKey(q.toLowerCase());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const addedPlayers = useMemo(() => addedPlayersByTeam[activeKey] || [], [activeKey, addedPlayersByTeam]);
  const players = useMemo(
    () => addedPlayers.map((p) => ({ last: p.playerLast, first: p.playerFirst, added: !p.isSeeded })),
    [addedPlayers]
  );
  const activeRows = rows[activeKey] || players.map(() => emptyRow());

  // Load rows from backend when tab changes
  useEffect(() => {
    if (access !== 'allowed') return undefined;
    let ignore = false;
    (async () => {
      try {
        // Load added players first so we can merge
        const addedResp = await api.get(`/roster/${activeKey}/players`);
        const added = addedResp.data;
        if (!ignore) setAddedPlayersByTeam((prev) => ({ ...prev, [activeKey]: added }));

        // Load equipment rows
        const resp = await api.get(`/roster/${activeKey}`);
        const server = resp.data;
        const map = new Map(server.map((s) => [`${s.playerLast}::${s.playerFirst}`.toLowerCase(), s]));
        const allPlayers = added.map((p) => ({ last: p.playerLast, first: p.playerFirst }));
        const merged = allPlayers.map((p) => {
          const hit = map.get(`${p.last}::${p.first}`.toLowerCase());
          if (!hit) return emptyRow();
          return {
            position: hit.position || '',
            jerseyNumber: hit.jerseyNumber || '',
            helmet: !!hit.helmet,
            shoulderPad: !!hit.shoulderPad,
            jersey: !!hit.jersey,
            practicePants: !!hit.practicePants,
            mouthPiece: !!hit.mouthPiece,
            dateIssued: hit.dateIssued || '',
            dateReturned: hit.dateReturned || '',
            playerInit: hit.playerInit || '',
            issuerInit: hit.issuerInit || '',
            feeHalfPaid: !!hit.feeHalfPaid,
            feePaidFull: !!hit.feePaidFull,
            notes: hit.notes || '',
            dob: hit.dob || '',
            city: hit.city || '',
            contactPhone: hit.contactPhone || '',
            emergencyContactName: hit.emergencyContactName || ''
          };
        });
        if (!ignore) setRows((prev) => ({ ...prev, [activeKey]: merged }));
      } catch (error) {
        if ([401, 403].includes(error.response?.status)) setAccess(error.response?.status === 401 ? 'signed-out' : 'denied');
        if (!ignore) setRows((prev) => ({ ...prev, [activeKey]: players.map(() => emptyRow()) }));
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, access]);

  const rosterUrl = typeof window !== 'undefined' ? `${window.location.origin}/roster` : '/roster';
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(rosterUrl)}&size=260x260&bgcolor=ffffff&color=0a1f44&margin=8`;

  const saveRow = useCallback(async (teamKey, player, row) => {
    setSaveState('saving');
    try {
      if (!canEditRoster) throw new Error('Roster is read-only');
      await api.post(`/roster/${teamKey}`, { playerLast: player.last, playerFirst: player.first, ...row });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1200);
    } catch (_e) {
      setSaveState('idle');
    }
  }, [canEditRoster]);

  const updateRow = (i, key, value) => {
    setRows((prev) => {
      const list = (prev[activeKey] || players.map(() => emptyRow())).map((r, idx) =>
        idx === i ? { ...r, [key]: value } : r
      );
      const next = { ...prev, [activeKey]: list };
      // Debounced save for this row
      const timerKey = `${activeKey}-${i}`;
      if (saveTimers.current[timerKey]) clearTimeout(saveTimers.current[timerKey]);
      saveTimers.current[timerKey] = setTimeout(() => {
        saveRow(activeKey, players[i], list[i]);
      }, 600);
      return next;
    });
  };

  const totals = useMemo(() => {
    const issued = activeRows.filter((r) => r.dateIssued && !r.dateReturned).length;
    const returned = activeRows.filter((r) => r.dateReturned).length;
    return { issued, returned, players: players.length };
  }, [activeRows, players.length]);

  const addPlayer = async (e) => {
    e.preventDefault();
    const first = newPlayer.first.trim();
    const last = newPlayer.last.trim();
    if (!first || !last) return;
    setAddingPlayer(true);
    try {
      if (!canEditRoster) throw new Error('Roster is read-only');
      await api.post(`/roster/${activeKey}/players`, { playerFirst: first, playerLast: last });
      // Refresh added players list
      const list = (await api.get(`/roster/${activeKey}/players`)).data;
      setAddedPlayersByTeam((prev) => ({ ...prev, [activeKey]: list }));
      // Ensure a row exists for the new player
      setRows((prev) => {
        const existing = prev[activeKey] || [];
        return { ...prev, [activeKey]: [...existing, emptyRow()] };
      });
      setNewPlayer({ first: '', last: '' });
      setShowAddForm(false);
    } catch (_e) {
      // no-op
    } finally {
      setAddingPlayer(false);
    }
  };

  const removeAddedPlayer = async (playerLast, playerFirst, rowIdx) => {
    if (!window.confirm(`Remove ${playerFirst} ${playerLast} from the roster?`)) return;
    if (!canEditRoster) return;
    await api.delete(`/roster/${activeKey}/players`, { params: { playerLast, playerFirst } });
    const list = (await api.get(`/roster/${activeKey}/players`)).data;
    setAddedPlayersByTeam((prev) => ({ ...prev, [activeKey]: list }));
    setRows((prev) => ({ ...prev, [activeKey]: (prev[activeKey] || []).filter((_, i) => i !== rowIdx) }));
  };

  if (access === 'checking') return <RosterAccessLoading />;
  if (access !== 'allowed') return <RosterAccessGate access={access} />;
  if (!rosterConfig) return <RosterAccessLoading />;

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-16 bg-[#0a1f44] overflow-hidden print:hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="team" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/80 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Certification Football Team Roster</div>
          <h1 className="font-display text-white text-5xl md:text-7xl leading-none" data-testid="roster-heading">{activeTeam.division.toUpperCase()}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-white/80 font-heading uppercase tracking-widest text-xs">
              <span>{rosterMeta.team}</span>
            <span className="text-[#c8102e]">•</span>
            <span>{rosterMeta.season}</span>
            <span className="text-[#c8102e]">•</span>
            <span data-testid="roster-team-id">Team ID: {activeTeam.teamId}</span>
          </div>
        </div>
      </section>

      {/* Team tabs */}
      <section className="bg-white border-b border-[#0a1f44]/10 print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap gap-2" data-testid="roster-tabs">
          {rosterTeamOrder.map((key) => (
            <button
              key={key}
              onClick={() => setActiveKey(key)}
              data-testid={`roster-tab-${key}`}
              className={`px-6 py-2.5 font-heading uppercase tracking-widest text-xs transition-colors border-2 ${
                activeKey === key
                  ? 'bg-[#c8102e] text-white border-[#c8102e]'
                  : 'bg-white text-[#0a1f44] border-[#0a1f44]/15 hover:border-[#c8102e]'
              }`}
            >
              {rosterTeams[key].label}
              {addedPlayersByTeam[key] && <span className="ml-2 opacity-60 text-[10px]">{addedPlayersByTeam[key].length}</span>}
            </button>
          ))}
        </div>
      </section>

      <section className={`${canEditRoster ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'} border-b print:hidden`} data-testid="roster-access-prompt">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className={`w-5 h-5 mt-0.5 ${canEditRoster ? 'text-emerald-700' : 'text-blue-700'}`} />
            <div>
              <div className={`font-heading uppercase tracking-widest text-xs ${canEditRoster ? 'text-emerald-800' : 'text-blue-800'}`} data-testid="roster-access-title">
                {canEditRoster ? 'VP Edit Mode' : 'Coach View Mode'}
              </div>
              <p className="text-sm text-[#0a1f44]/70 mt-1" data-testid="roster-access-description">
                {canEditRoster ? `Signed in as ${viewer.name}. Changes auto-save.` : `Signed in as ${viewer.name}. Roster access is view-only.`}
              </p>
            </div>
          </div>
          <button type="button" onClick={() => { clearToken(); setViewer(null); setAccess('signed-out'); }} data-testid="roster-editor-logout" className={`inline-flex items-center gap-2 self-start border px-4 py-2 font-heading uppercase tracking-widest text-[10px] transition-colors ${canEditRoster ? 'border-emerald-700 text-emerald-800 hover:bg-emerald-700' : 'border-blue-700 text-blue-800 hover:bg-blue-700'} hover:text-white`}><LogOut className="w-3.5 h-3.5" /> {canEditRoster ? 'Exit Edit Mode' : 'Sign Out'}</button>
        </div>
      </section>

      {/* Toolbar with QR + print */}
      <section className="bg-white border-b border-[#0a1f44]/10 print:bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="hidden sm:flex flex-col items-center print:flex">
              <img
                src={qrSrc}
                alt="QR code — scan to open this roster"
                className="w-24 h-24 border-2 border-[#0a1f44]/10 p-1 bg-white"
                data-testid="roster-qr-code"
              />
              <div className="font-heading uppercase tracking-widest text-[9px] text-[#0a1f44]/60 mt-2 flex items-center gap-1">
                <QrCode className="w-3 h-3" /> Scan Roster
              </div>
            </div>
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-1">Equipment Log</div>
              <div className="font-display text-[#0a1f44] text-2xl md:text-3xl leading-tight">EQUIPMENT ISSUE &amp; RETURN</div>
              <div className="mt-2 flex gap-4 text-xs font-heading uppercase tracking-widest text-[#0a1f44]/60 items-center">
                <span data-testid="roster-count-players">{totals.players} PLAYERS</span>
                <span className="text-[#c8102e]" data-testid="roster-count-issued">{totals.issued} ISSUED</span>
                <span className="text-emerald-700" data-testid="roster-count-returned">{totals.returned} RETURNED</span>
                {saveState !== 'idle' && (
                  <span className="flex items-center gap-1 text-[#0a1f44]/50 normal-case" data-testid="roster-save-state">
                    {saveState === 'saving' ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                    ) : (
                      <><Check className="w-3 h-3 text-emerald-600" /> Saved</>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="roster-print-btn"
            className="print:hidden inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-xs px-6 py-3 transition-colors"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>
      </section>

      {/* Roster table */}
      <section className="py-10 bg-[#f5f6f8] print:py-2 print:bg-white">
        <div className="max-w-[1400px] mx-auto px-4">
          <fieldset disabled={!canEditRoster} data-testid="roster-edit-fieldset" className={`min-w-0 w-full ${!canEditRoster ? '[&_input]:bg-transparent [&_input]:border-transparent [&_input]:text-[#0a1f44] [&_input]:disabled:opacity-100' : ''}`}>
          {/* Add Player toolbar (always visible; enables adding to empty teams too) */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60">
              {players.length} Players · {addedPlayers.length} Added Manually
            </div>
            {canEditRoster && (!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                data-testid="roster-add-player-btn"
                className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
              >
                <UserPlus className="w-3 h-3" /> Add Player
              </button>
            ) : (
              <form onSubmit={addPlayer} className="flex items-center gap-2" data-testid="roster-add-player-form">
                <input
                  type="text"
                  placeholder="First"
                  value={newPlayer.first}
                  onChange={(e) => setNewPlayer({ ...newPlayer, first: e.target.value })}
                  data-testid="roster-add-first"
                  className="border border-[#0a1f44]/20 px-3 py-2 text-sm w-32 focus:outline-none focus:border-[#c8102e]"
                  autoFocus
                />
                <input
                  type="text"
                  placeholder="Last"
                  value={newPlayer.last}
                  onChange={(e) => setNewPlayer({ ...newPlayer, last: e.target.value })}
                  data-testid="roster-add-last"
                  className="border border-[#0a1f44]/20 px-3 py-2 text-sm w-36 focus:outline-none focus:border-[#c8102e]"
                />
                <button
                  type="submit"
                  disabled={addingPlayer}
                  data-testid="roster-add-player-save"
                  className="bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
                >
                  {addingPlayer ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setNewPlayer({ first: '', last: '' }); }}
                  className="text-[#0a1f44]/50 hover:text-[#c8102e] p-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </form>
            ))}
          </div>

          {players.length === 0 ? (
            <div className="bg-white border border-[#0a1f44]/10 p-16 text-center" data-testid="roster-empty-state">
              <Trophy className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
              <div className="font-display text-[#0a1f44] text-3xl mb-2">{activeTeam.division.toUpperCase()} ROSTER COMING SOON</div>
              <p className="text-[#0a1f44]/60 max-w-xl mx-auto font-heading tracking-wider text-sm">
                The {activeTeam.label} team roster hasn't been uploaded yet. Once the coaching staff submits the certification form, players will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="hidden md:block overflow-x-auto max-w-full bg-white border border-[#0a1f44]/10">
              <table className="w-full text-sm border-collapse" data-testid="roster-table">
                <thead className="bg-[#0a1f44] text-white">
                  <tr>
                    <th className="px-2 py-3 text-left font-heading uppercase tracking-widest text-[10px]">#</th>
                    <th className="px-2 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Player</th>
                    <th className="px-2 py-3 text-left font-heading uppercase tracking-widest text-[10px]">Position</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Jersey #</th>
                    {EQUIPMENT_COLUMNS.map((c) => (
                      <th key={c.key} className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">{c.label}</th>
                    ))}
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Date Issued</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Date Returned</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Player Init.</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Issuer Init.</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Fee Status</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Half Paid</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Paid In Full</th>
                    <th className="px-2 py-3 text-center font-heading uppercase tracking-widest text-[10px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p, i) => {
                    const r = activeRows[i] || emptyRow();
                    return (
                      <tr
                        key={`${p.last}-${p.first}`}
                        data-testid={`roster-row-${i}`}
                        className={i % 2 === 0 ? 'bg-white' : 'bg-[#0a1f44]/[0.03]'}
                      >
                        <td className="px-2 py-2 text-[#0a1f44]/60 font-heading">{i + 1}</td>
                        <td className="px-2 py-2 font-heading text-[#0a1f44] whitespace-nowrap">
                          <button
                            onClick={() => setDetailIdx(i)}
                            data-testid={`roster-player-btn-${i}`}
                            className="text-left hover:text-[#c8102e] transition-colors"
                          >
                            <span className="font-semibold underline decoration-transparent hover:decoration-[#c8102e]">{p.last},</span> {p.first}
                          </button>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            value={r.position}
                            onChange={(e) => updateRow(i, 'position', e.target.value)}
                            placeholder="—"
                            className="w-20 border border-[#0a1f44]/15 px-2 py-1 text-xs uppercase tracking-wider text-center focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-position-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="text"
                            value={r.jerseyNumber}
                            onChange={(e) => updateRow(i, 'jerseyNumber', e.target.value)}
                            placeholder="—"
                            className="w-14 border border-[#0a1f44]/15 px-2 py-1 text-xs text-center focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-jersey-num-${i}`}
                          />
                        </td>
                        {EQUIPMENT_COLUMNS.map((c) => (
                          <td key={c.key} className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={r[c.key]}
                              onChange={(e) => updateRow(i, c.key, e.target.checked)}
                              className="w-4 h-4 accent-[#c8102e] cursor-pointer"
                              data-testid={`roster-${c.key}-${i}`}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center">
                          <input
                            type="date"
                            value={r.dateIssued}
                            onChange={(e) => updateRow(i, 'dateIssued', e.target.value)}
                            className="border border-[#0a1f44]/15 px-1 py-1 text-xs focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-date-issued-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="date"
                            value={r.dateReturned}
                            onChange={(e) => updateRow(i, 'dateReturned', e.target.value)}
                            className="border border-[#0a1f44]/15 px-1 py-1 text-xs focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-date-returned-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="text"
                            maxLength={3}
                            value={r.playerInit}
                            onChange={(e) => updateRow(i, 'playerInit', e.target.value.toUpperCase())}
                            placeholder="___"
                            className="w-14 border border-[#0a1f44]/15 px-2 py-1 text-xs text-center uppercase tracking-wider focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-player-init-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="text"
                            maxLength={3}
                            value={r.issuerInit}
                            onChange={(e) => updateRow(i, 'issuerInit', e.target.value.toUpperCase())}
                            placeholder="___"
                            className="w-14 border border-[#0a1f44]/15 px-2 py-1 text-xs text-center uppercase tracking-wider focus:outline-none focus:border-[#c8102e]"
                            data-testid={`roster-issuer-init-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          {r.feePaidFull ? (
                            <span className="inline-flex bg-emerald-100 text-emerald-800 font-heading uppercase tracking-widest text-[9px] px-2 py-1" data-testid={`roster-fee-status-${i}`}>Paid In Full</span>
                          ) : r.feeHalfPaid ? (
                            <span className="inline-flex bg-amber-100 text-amber-800 font-heading uppercase tracking-widest text-[9px] px-2 py-1" data-testid={`roster-fee-status-${i}`}>Half Paid</span>
                          ) : (
                            <span className="inline-flex bg-[#c8102e]/10 text-[#c8102e] font-heading uppercase tracking-widest text-[9px] px-2 py-1" data-testid={`roster-fee-status-${i}`}>Unpaid</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={r.feeHalfPaid}
                            onChange={(e) => updateRow(i, 'feeHalfPaid', e.target.checked)}
                            className="w-4 h-4 accent-amber-500 cursor-pointer"
                            data-testid={`roster-fee-half-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={r.feePaidFull}
                            onChange={(e) => updateRow(i, 'feePaidFull', e.target.checked)}
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                            data-testid={`roster-fee-full-${i}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          {p.added && (
                            <button
                              onClick={() => removeAddedPlayer(p.last, p.first, i)}
                              data-testid={`roster-remove-${i}`}
                              title="Remove added player"
                              className="p-1.5 text-[#c8102e]/60 hover:text-white hover:bg-[#c8102e] transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Signature line */}
          {players.length > 0 && (
            <div className="hidden md:grid mt-8 grid-cols-1 md:grid-cols-2 gap-8 print:mt-4 print:grid">
              <div>
                <div className="border-b border-[#0a1f44] h-10" />
                <div className="mt-2 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60">Equipment Manager Signature</div>
              </div>
              <div>
                <div className="border-b border-[#0a1f44] h-10" />
                <div className="mt-2 font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60">Date</div>
              </div>
            </div>
          )}
          </fieldset>

          {players.length > 0 && (
            <div className="md:hidden space-y-3" data-testid="roster-mobile-list">
              {players.map((player, index) => {
                const row = activeRows[index] || emptyRow();
                const equipmentCount = EQUIPMENT_COLUMNS.filter((item) => row[item.key]).length;
                return (
                  <article key={`${player.last}-${player.first}`} className="bg-white border border-[#0a1f44]/10 p-4" data-testid={`roster-mobile-card-${index}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display text-xl text-[#0a1f44] break-words">{player.first} {player.last}</div>
                        <div className="text-sm text-[#0a1f44]/60 mt-1">{row.position || 'Position TBA'}{row.jerseyNumber ? ` · #${row.jerseyNumber}` : ''}</div>
                      </div>
                      <span className={`shrink-0 font-heading uppercase text-[9px] px-2 py-1 ${row.feePaidFull ? 'bg-emerald-100 text-emerald-800' : row.feeHalfPaid ? 'bg-amber-100 text-amber-800' : 'bg-[#c8102e]/10 text-[#c8102e]'}`} data-testid={`roster-mobile-fee-${index}`}>
                        {row.feePaidFull ? 'Paid' : row.feeHalfPaid ? 'Half Paid' : 'Unpaid'}
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#0a1f44]/10 pt-3">
                      <div className="text-xs text-[#0a1f44]/60 font-heading uppercase" data-testid={`roster-mobile-equipment-${index}`}>{equipmentCount}/{EQUIPMENT_COLUMNS.length} Equipment</div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setDetailIdx(index)} data-testid={`roster-mobile-details-${index}`} className="min-h-11 bg-[#0a1f44] text-white px-4 py-2 font-heading uppercase text-[10px]">View Details</button>
                        {canEditRoster && player.added && (
                          <button type="button" onClick={() => removeAddedPlayer(player.last, player.first, index)} data-testid={`roster-mobile-remove-${index}`} aria-label={`Remove ${player.first} ${player.last}`} className="w-11 h-11 flex items-center justify-center border border-[#c8102e] text-[#c8102e]"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <p className="mt-8 text-xs text-[#0a1f44]/50 max-w-3xl font-heading tracking-wider print:hidden">
            <Trophy className="w-3 h-3 inline mr-1" />
            {canEditRoster ? 'Private player details are visible in VP Edit Mode. Changes auto-save as you type.' : 'Private roster information is available in authenticated Coach View Mode. Editing remains locked.'}
          </p>
        </div>
      </section>

      {/* Player Detail Modal */}
      {detailIdx !== null && players[detailIdx] && (() => {
        const p = players[detailIdx];
        const r = activeRows[detailIdx] || emptyRow();
        const equipmentItems = [
          { key: 'helmet', label: 'Helmet' },
          { key: 'shoulderPad', label: 'Shoulder Pad' },
          { key: 'jersey', label: 'Jersey #' },
          { key: 'practicePants', label: 'Practice Pants' },
          { key: 'mouthPiece', label: 'Mouth Piece' }
        ];
        const issuedCount = equipmentItems.filter((it) => r[it.key]).length;
        return (
          <div
            className="fixed inset-0 z-[60] bg-[#0a1f44]/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setDetailIdx(null)}
            data-testid="player-detail-modal"
          >
            <div
              className="bg-white w-full max-w-3xl border-t-4 border-[#c8102e] max-h-[92vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between p-6 bg-[#0a1f44] text-white">
                <div>
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Player Detail · {activeTeam.label}</div>
                  <div className="font-display text-3xl md:text-4xl leading-tight mt-1" data-testid="player-detail-name">{p.first} {p.last}</div>
                  <div className="mt-2 flex gap-3 text-white/60 font-heading uppercase tracking-widest text-[10px]">
                    <span>{r.position || 'Position TBA'}</span>
                    {r.jerseyNumber && <span className="text-[#c8102e]">#{r.jerseyNumber}</span>}
                  </div>
                </div>
                <button
                  onClick={() => setDetailIdx(null)}
                  data-testid="player-detail-close"
                  className="p-2 text-white/60 hover:text-[#c8102e] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Fee Status */}
                <div className="border-l-4 border-[#c8102e] bg-[#0a1f44]/[0.03] p-4">
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-3">Fee Status</div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div>
                      {r.feePaidFull ? (
                        <span className="inline-flex bg-emerald-100 text-emerald-800 font-heading uppercase tracking-widest text-xs px-3 py-1.5" data-testid="player-detail-fee-status">Paid In Full</span>
                      ) : r.feeHalfPaid ? (
                        <span className="inline-flex bg-amber-100 text-amber-800 font-heading uppercase tracking-widest text-xs px-3 py-1.5" data-testid="player-detail-fee-status">Half Paid</span>
                      ) : (
                        <span className="inline-flex bg-[#c8102e]/10 text-[#c8102e] font-heading uppercase tracking-widest text-xs px-3 py-1.5" data-testid="player-detail-fee-status">Unpaid</span>
                      )}
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        disabled={!canEditRoster}
                        checked={r.feeHalfPaid}
                        onChange={(e) => updateRow(detailIdx, 'feeHalfPaid', e.target.checked)}
                        data-testid="player-detail-fee-half"
                        className="w-4 h-4 accent-amber-500"
                      />
                      Half Paid
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        disabled={!canEditRoster}
                        checked={r.feePaidFull}
                        onChange={(e) => updateRow(detailIdx, 'feePaidFull', e.target.checked)}
                        data-testid="player-detail-fee-full"
                        className="w-4 h-4 accent-emerald-600"
                      />
                      Paid In Full
                    </label>
                  </div>
                </div>

                {/* Equipment Log */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Equipment Log</div>
                    <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60" data-testid="player-detail-equipment-count">{issuedCount} / {equipmentItems.length} items</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    {equipmentItems.map((it) => (
                      <label key={it.key} className="flex items-center gap-2 p-3 bg-[#0a1f44]/[0.03] hover:bg-[#0a1f44]/[0.06] cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          disabled={!canEditRoster}
                          checked={r[it.key]}
                          onChange={(e) => updateRow(detailIdx, it.key, e.target.checked)}
                          data-testid={`player-detail-${it.key}`}
                          className="w-4 h-4 accent-[#c8102e]"
                        />
                        <span className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]">{it.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Date of Birth</label>
                      <input
                        type="date"
                        disabled={!canEditRoster}
                        value={r.dob}
                        onChange={(e) => updateRow(detailIdx, 'dob', e.target.value)}
                        data-testid="player-detail-dob"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">City / ZIP</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        value={r.city}
                        onChange={(e) => updateRow(detailIdx, 'city', e.target.value)}
                        placeholder="e.g. Hingham, MA 02043"
                        data-testid="player-detail-city"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Emergency Contact Name</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        value={r.emergencyContactName}
                        onChange={(e) => updateRow(detailIdx, 'emergencyContactName', e.target.value)}
                        placeholder="e.g. Parent / Guardian full name"
                        data-testid="player-detail-emergency-contact-name"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Emergency Contact Phone</label>
                      <input
                        type="tel"
                        disabled={!canEditRoster}
                        value={r.contactPhone}
                        onChange={(e) => updateRow(detailIdx, 'contactPhone', e.target.value)}
                        placeholder="e.g. 212-555-0100"
                        data-testid="player-detail-contact-phone"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Position</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        value={r.position}
                        onChange={(e) => updateRow(detailIdx, 'position', e.target.value)}
                        data-testid="player-detail-position"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Jersey #</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        value={r.jerseyNumber}
                        onChange={(e) => updateRow(detailIdx, 'jerseyNumber', e.target.value)}
                        data-testid="player-detail-jersey"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Date Issued</label>
                      <input
                        type="date"
                        disabled={!canEditRoster}
                        value={r.dateIssued}
                        onChange={(e) => updateRow(detailIdx, 'dateIssued', e.target.value)}
                        data-testid="player-detail-date-issued"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Date Returned</label>
                      <input
                        type="date"
                        disabled={!canEditRoster}
                        value={r.dateReturned}
                        onChange={(e) => updateRow(detailIdx, 'dateReturned', e.target.value)}
                        data-testid="player-detail-date-returned"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Player Initials</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        maxLength={3}
                        value={r.playerInit}
                        onChange={(e) => updateRow(detailIdx, 'playerInit', e.target.value.toUpperCase())}
                        data-testid="player-detail-player-init"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1">Issuer Initials</label>
                      <input
                        type="text"
                        disabled={!canEditRoster}
                        maxLength={3}
                        value={r.issuerInit}
                        onChange={(e) => updateRow(detailIdx, 'issuerInit', e.target.value.toUpperCase())}
                        data-testid="player-detail-issuer-init"
                        className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#c8102e]"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-2">Notes</div>
                  <textarea
                    rows={4}
                    disabled={!canEditRoster}
                    value={r.notes}
                    onChange={(e) => updateRow(detailIdx, 'notes', e.target.value)}
                    placeholder="Coach notes — attendance, injuries, family comms, uniform sizing…"
                    data-testid="player-detail-notes"
                    className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#c8102e]"
                  />
                </div>

                <div className="flex items-center justify-between text-xs text-[#0a1f44]/50 pt-2 border-t border-[#0a1f44]/10">
                  <span className="flex items-center gap-1">{canEditRoster ? <><Check className="w-3 h-3 text-emerald-600" /> Auto-saves as you type</> : <><LockKeyhole className="w-3 h-3 text-blue-700" /> Read-only Coach view</>}</span>
                  <button
                    onClick={() => setDetailIdx(null)}
                    className="bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
                    data-testid="player-detail-done"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const RosterAccessLoading = () => (
  <div className="min-h-[70vh] bg-[#060f2a] flex items-center justify-center pt-24" data-testid="roster-access-loading">
    <Loader2 className="w-8 h-8 text-[#c8102e] animate-spin" aria-label="Checking roster access" />
  </div>
);

const RosterAccessGate = ({ access }) => (
  <div className="min-h-[78vh] bg-[#060f2a] flex items-center justify-center px-6 pt-28 pb-16 font-body" data-testid="roster-lock-screen">
    <section className="w-full max-w-3xl border border-white/15 bg-[#0a1f44] px-7 py-12 sm:px-12 sm:py-16 text-center shadow-[10px_10px_0_#c8102e]">
      <div className="w-16 h-16 mx-auto border border-[#f4b400]/50 bg-[#060f2a] flex items-center justify-center text-[#f4b400]" data-testid="roster-lock-icon"><LockKeyhole className="w-8 h-8" /></div>
      <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mt-8">Authorized Coaching Staff Only</div>
      <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="roster-lock-heading">RESTRICTED ROSTER ACCESS</h1>
      <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-xl mx-auto mt-5" data-testid="roster-lock-description">Roster information is confidential and available only to active Coach accounts and the authorized Vice President.</p>
      {access === 'denied' && <p className="text-[#f4b400] text-xs font-heading uppercase tracking-widest mt-4" data-testid="roster-lock-unauthorized-message">Your current account does not have roster access.</p>}
      <Link to="/admin/login?next=/roster" onClick={() => clearToken()} data-testid="roster-coach-login-btn" className="inline-flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-7 py-3 mt-8 transition-colors"><LockKeyhole className="w-4 h-4" /> Coach Login</Link>
    </section>
  </div>
);
